import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { Map } from "immutable";
import { Button } from "reactstrap";

import { UserTable } from "@auth/components/UserTable";
import { User, Role, Organisation } from "@auth/models";
import { messages } from "@auth/messages";
import { Api } from "@api/index";
import { Page, PageTitle } from "@ui/Page";
import { createApiComponent, ApiComponentProps } from "@api/components/ApiComponent";
import { Locale } from "@localisation/models";

interface UsersIndexStateProps {
    users: Map<number, User>;
    organisations: Map<number, Organisation>;
    roles: Map<string, Role>;
    usersLastLoaded: Date;
    userId: number;
    canCreateUser: boolean;
    canUpdateUser: boolean;
    canDeleteUser: boolean;
    locale: Locale;
}

export const UsersIndex = ({
    users,
    organisations,
    roles,
    userId,
    canCreateUser,
    canUpdateUser,
    canDeleteUser,
    locale,
    usersLastLoaded,
}: UsersIndexStateProps) => {
    const intl = useIntl();
    const createButtonProps = {
        tag: Link,
        to: "/users/create",
        color: "primary",
    };
    const ApiComponent = useRef<React.ComponentClass<ApiComponentProps>>(null);
    if (ApiComponent.current == null) {
        ApiComponent.current = createApiComponent({
            actionCreators: [Api.usersIndex],
            lastLoaded: [usersLastLoaded],
        });
    }
    const breadcrumbItems = [
        { text: <FormattedMessage {...messages.userEntityPlural} /> },
    ];

    return (
        <Page className="users-page" breadcrumbs={breadcrumbItems}>
            <PageTitle tag="h1" className="display-2">
                <FormattedMessage {...messages.userEntityPlural} />
            </PageTitle>
            <p className="sub-heading"><FormattedMessage {...messages.usersBlurb} /></p>
            { canCreateUser &&
                <Button {...createButtonProps}>
                    <FormattedMessage {...messages.usersCreateButton} />
                </Button>
            }
            <ApiComponent.current>
                <UserTable
                    key="card-list"
                    users={users}
                    organisations={organisations}
                    roles={roles}
                    intl={intl}
                    userId={userId}
                    canUpdateUser={canUpdateUser}
                    canDeleteUser={canDeleteUser}
                    locale={locale}></UserTable>
            </ApiComponent.current>
        </Page>
    );
};

export type { UsersIndexStateProps };
